import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  ShoppingCart,
  Search,
  Eye,
  Download,
  Truck,
  Package,
  CheckCircle2,
  ExternalLink,
  Printer,
  Navigation,
  FileText,
  Clock,
  Send,
  Sparkles,
  MapPin,
  RefreshCw,
  X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import AdminPagination from '../../components/admin/AdminPagination';
import { fetchAllAdminPages } from '../../utils/fetchAllAdminPages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [srFilter, setSrFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const requestId = useRef(0);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Shiprocket action loading states
  const [actionLoading, setActionLoading] = useState(false);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); setDebouncedSearch(searchQuery.trim()); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const queryParams = React.useMemo(() => ({
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(srFilter !== 'all' ? { shiprocket_status: srFilter } : {}),
    search: debouncedSearch
  }), [statusFilter, srFilter, debouncedSearch]);

  const fetchOrders = useCallback(async () => {
    const currentRequest = ++requestId.current;
    try {
      const response = await axios.get(`${API}/orders/admin/all`, { 
        params: { ...queryParams, page },
        withCredentials: true 
      });
      if (currentRequest !== requestId.current) return;
      setOrders(response.data.items);
      setTotal(response.data.total);
      setPages(response.data.pages);
      if (page > Math.max(1, response.data.pages)) setPage(Math.max(1, response.data.pages));
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [queryParams, page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, 
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // ── Shiprocket Actions ──

  // 1. Create Shiprocket Order
  const handleCreateShiprocket = async (orderId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/shipping/shiprocket/create/${orderId}`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Shiprocket order created');
      await fetchOrders();
      // Update selected order in state
      const updated = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      setSelectedOrder(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order on Shiprocket');
    } finally {
      setActionLoading(false);
    }
  };

  // 2. Generate AWB
  const handleAssignAWB = async (orderId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/shipping/shiprocket/assign-awb/${orderId}`, {}, { withCredentials: true });
      toast.success(`AWB Assigned: ${res.data.awb_code || 'Generated'}`);
      await fetchOrders();
      const updated = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      setSelectedOrder(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AWB');
    } finally {
      setActionLoading(false);
    }
  };

  // 3. Request Pickup
  const handleRequestPickup = async (orderId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/shipping/shiprocket/request-pickup/${orderId}`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Pickup scheduled successfully');
      await fetchOrders();
      const updated = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      setSelectedOrder(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request pickup');
    } finally {
      setActionLoading(false);
    }
  };

  // 4. Download Shipping Label
  const handleDownloadLabel = async (orderId) => {
    setActionLoading(true);
    try {
      const res = await axios.get(`${API}/shipping/shiprocket/label/${orderId}`, { withCredentials: true });
      if (res.data.label_url) {
        window.open(res.data.label_url, '_blank');
        toast.success('Opened Shiprocket shipping label');
      } else {
        toast.info('Label generated');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get shipping label');
    } finally {
      setActionLoading(false);
    }
  };

  // 5. Track Live Modal
  const handleOpenLiveTracking = async (orderId) => {
    setTrackingModalOpen(true);
    setTrackingLoading(true);
    try {
      const res = await axios.get(`${API}/shipping/track/${orderId}`);
      setTrackingData(res.data);
    } catch (error) {
      toast.error('Unable to fetch live tracking');
    } finally {
      setTrackingLoading(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
    paid: 'bg-green-100 text-green-700 border-green-200',
    processing: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    shipped: 'bg-purple-100 text-purple-700 border-purple-200',
    delivered: 'bg-teal-100 text-teal-700 border-teal-200',
    cancelled: 'bg-red-100 text-red-700 border-red-200'
  };

  const srStatusColors = {
    unshipped: 'bg-gray-100 text-gray-600',
    PROCESSING: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    AWB_ASSIGNED: 'bg-purple-50 text-purple-700 border-purple-200',
    PICKUP_SCHEDULED: 'bg-blue-50 text-blue-700 border-blue-200',
    IN_TRANSIT: 'bg-amber-50 text-amber-700 border-amber-200',
    DELIVERED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    CANCELLED: 'bg-red-50 text-red-700 border-red-200'
  };

  const filteredOrders = orders;

  const exportAllOrders = async () => {
    try {
      const allOrders = await fetchAllAdminPages(`${API}/orders/admin/all`, queryParams);
      downloadCSV(allOrders);
    } catch (error) {
      toast.error('Failed to export orders');
    }
  };

  // ── CSV Export ──
  const downloadCSV = (dataToExport) => {
    if (!dataToExport.length) {
      toast.error('No orders to export');
      return;
    }

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const headers = [
      'Order ID',
      'Date',
      'Customer Name',
      'Customer Email',
      'Phone',
      'Address',
      'City',
      'State',
      'Pincode',
      'Shipping Package',
      'Courier Name',
      'AWB Code',
      'Shiprocket Status',
      'Shipping Cost (Rs)',
      'Items (Name x Qty @ Price)',
      'Item Count',
      'Subtotal (Rs)',
      'Discount (Rs)',
      'Coupon Code',
      'Total Amount (Rs)',
      'Payment Method',
      'Order Status',
      'Payment Status'
    ];

    const rows = dataToExport.map(order => {
      const addr = order.shipping_address || {};
      const itemsSummary = (order.items || []).map(
        i => `${i.name} x${i.quantity} @ Rs${i.price}`
      ).join(' | ');

      return [
        order.id,
        order.created_at ? new Date(order.created_at).toLocaleString('en-IN') : '',
        addr.name || order.user?.name || '',
        order.user?.email || '',
        addr.phone || '',
        addr.address || '',
        addr.city || '',
        addr.state || '',
        addr.pincode || '',
        order.shipping_package?.name || 'Standard Sacred Delivery',
        order.courier_name || order.shipping_package?.courier_name || '',
        order.awb_code || '',
        order.shiprocket_status || 'unshipped',
        order.shipping_cost || 0,
        itemsSummary,
        (order.items || []).length,
        order.subtotal || '',
        order.discount || 0,
        order.coupon_code || '',
        order.total_amount || '',
        order.payment_method || '',
        order.status || '',
        order.payment_status || ''
      ].map(escapeCSV).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${dataToExport.length} orders as CSV`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Header & Filters */}
      <div className="bg-white rounded-xl p-4 shadow-xs border border-gray-100 space-y-3">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by email, devotee name, order ID, or AWB tracking..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 text-xs sm:text-sm"
              data-testid="order-search"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => downloadCSV(filteredOrders)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-2xs"
              title="Download current view as CSV"
              data-testid="download-filtered-csv"
            >
              <Download className="w-3.5 h-3.5" />
              Export ({filteredOrders.length})
            </button>
            <button
              onClick={exportAllOrders}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#4E1225] hover:bg-[#6E1B34] text-white rounded-lg text-xs font-semibold transition-colors shadow-2xs"
              title="Download all matching orders as CSV"
              data-testid="download-all-csv"
            >
              <Download className="w-3.5 h-3.5" />
              Export All ({total})
            </button>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100">
          <div className="flex gap-1.5 flex-wrap items-center">
            <span className="text-[11px] font-bold text-gray-400 uppercase mr-1">Status:</span>
            {['all', 'pending', 'paid', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => { setStatusFilter(status); setPage(1); }}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  statusFilter === status
                    ? 'bg-[#6E1B34] text-white shadow-2xs'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`filter-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Shiprocket Filter */}
          <div className="flex items-center gap-1.5 text-xs">
            <Truck className="w-3.5 h-3.5 text-[#D9711F]" />
            <span className="font-semibold text-gray-600">Shiprocket:</span>
            <select
              value={srFilter}
              onChange={(e) => { setSrFilter(e.target.value); setPage(1); }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#6E1B34]"
            >
              <option value="all">All Shipments</option>
              <option value="unshipped">Unshipped</option>
              <option value="PROCESSING">Processing</option>
              <option value="AWB_ASSIGNED">AWB Assigned</option>
              <option value="PICKUP_SCHEDULED">Pickup Scheduled</option>
              <option value="IN_TRANSIT">In Transit</option>
              <option value="DELIVERED">Delivered</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-xs border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#FAF7F0] border-b border-gray-100 text-[11px] font-bold text-[#83725B] uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Order ID</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Shipping Package & Courier</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Order Status</th>
                <th className="px-4 py-3">Shiprocket</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-xs">
              {filteredOrders.map((order) => (
                <motion.tr
                  key={order.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50/80 transition-colors"
                  data-testid={`order-row-${order.id}`}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono font-bold text-[#4E1225]">#{order.id?.slice(0, 10)}</span>
                  </td>

                  <td className="px-4 py-3">
                    <div>
                      <p className="font-bold text-[#2A1F16]">{order.shipping_address?.name || 'Devotee'}</p>
                      <p className="text-[11px] text-gray-500">{order.shipping_address?.city}, {order.shipping_address?.pincode}</p>
                    </div>
                  </td>

                  {/* Selected Package & Courier */}
                  <td className="px-4 py-3">
                    <div>
                      <span className="font-semibold text-[#4E1225] block truncate max-w-[180px]">
                        {order.shipping_package?.name || 'Standard Sacred Delivery'}
                      </span>
                      <p className="text-[11px] text-[#83725B] flex items-center gap-1">
                        <Truck className="w-3 h-3 text-[#D9711F]" />
                        <span>{order.courier_name || order.shipping_package?.courier_name || 'Delhivery / Express'}</span>
                      </p>
                    </div>
                  </td>

                  <td className="px-4 py-3 text-gray-600">
                    {order.items?.length || 0} items
                  </td>

                  <td className="px-4 py-3">
                    <span className="font-bold text-[#6E1B34]">₹{order.total_amount?.toLocaleString()}</span>
                  </td>

                  {/* Order Status */}
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusColors[order.status] || 'bg-gray-100 text-gray-600'}`}>
                      {order.status}
                    </span>
                  </td>

                  {/* Shiprocket Status */}
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                      srStatusColors[order.shiprocket_status] || 'bg-gray-100 text-gray-600'
                    }`}>
                      {order.shiprocket_status || 'unshipped'}
                    </span>
                    {order.awb_code && (
                      <span className="block font-mono text-[9px] text-[#83725B] mt-0.5">
                        {order.awb_code}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {new Date(order.created_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <select
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        className="text-xs border border-gray-200 rounded-md px-1.5 py-1 bg-white font-medium focus:ring-1 focus:ring-[#6E1B34]"
                        data-testid={`status-select-${order.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="paid">Paid</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedOrder(order)}
                        className="h-7 w-7 p-0 text-[#6E1B34] hover:bg-[#FBF4E7]"
                        data-testid={`view-order-${order.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <ShoppingCart className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-xs">No orders found matching filters</p>
          </div>
        )}
      </div>

      <AdminPagination page={page} pages={pages} total={total} onPageChange={setPage} />

      {/* Order Detail Modal with Shiprocket Controls */}
      <AnimatePresence>
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-2xs flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl p-5 sm:p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-[#E7D9BC] shadow-xl text-xs space-y-4"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                <div>
                  <h3 className="font-serif-heading text-lg font-bold text-[#4E1225]">
                    Order #{selectedOrder.id}
                  </h3>
                  <p className="text-gray-500 text-[11px]">
                    Placed on {new Date(selectedOrder.created_at).toLocaleString('en-IN')}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {/* Shiprocket Fulfillment Controls Panel */}
              <div className="bg-[#FBF4E7] border border-[#E7D9BC] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-[#6E1B34] text-white">
                      <Truck className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-serif-heading font-bold text-sm text-[#4E1225]">
                        Shiprocket Logistics Manager
                      </h4>
                      <p className="text-[11px] text-[#83725B]">
                        Selected: <strong>{selectedOrder.shipping_package?.name || 'Standard Delivery'}</strong> • {selectedOrder.shipping_package?.etd || '3-5 days'}
                      </p>
                    </div>
                  </div>

                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                    srStatusColors[selectedOrder.shiprocket_status] || 'bg-gray-100 text-gray-600'
                  }`}>
                    {selectedOrder.shiprocket_status || 'unshipped'}
                  </span>
                </div>

                {/* Shiprocket Info Badges */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 bg-white/80 p-3 rounded-lg border border-[#E7D9BC]/60">
                  <div>
                    <span className="text-[10px] text-gray-500 uppercase block">Assigned Courier</span>
                    <span className="font-bold text-[#4E1225]">{selectedOrder.courier_name || selectedOrder.shipping_package?.courier_name || 'Delhivery Express'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-500 uppercase block">AWB Tracking Code</span>
                    <span className="font-mono font-bold text-[#6E1B34]">{selectedOrder.awb_code || 'Not Assigned Yet'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-500 uppercase block">Shipment ID</span>
                    <span className="font-mono text-gray-700">{selectedOrder.shiprocket_shipment_id || selectedOrder.shiprocket_order_id || 'Pending creation'}</span>
                  </div>
                </div>

                {/* Shiprocket Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {!selectedOrder.shiprocket_order_id && (
                    <Button
                      size="sm"
                      onClick={() => handleCreateShiprocket(selectedOrder.id)}
                      disabled={actionLoading}
                      className="bg-[#6E1B34] hover:bg-[#4E1225] text-white text-xs font-bold"
                    >
                      <Package className="w-3.5 h-3.5 mr-1" />
                      Create Shiprocket Order
                    </Button>
                  )}

                  {selectedOrder.shiprocket_order_id && !selectedOrder.awb_code && (
                    <Button
                      size="sm"
                      onClick={() => handleAssignAWB(selectedOrder.id)}
                      disabled={actionLoading}
                      className="bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold"
                    >
                      <FileText className="w-3.5 h-3.5 mr-1" />
                      Assign Courier & Generate AWB
                    </Button>
                  )}

                  {selectedOrder.awb_code && selectedOrder.shiprocket_status !== 'PICKUP_SCHEDULED' && (
                    <Button
                      size="sm"
                      onClick={() => handleRequestPickup(selectedOrder.id)}
                      disabled={actionLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
                    >
                      <Send className="w-3.5 h-3.5 mr-1" />
                      Request Courier Pickup
                    </Button>
                  )}

                  {selectedOrder.shiprocket_shipment_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadLabel(selectedOrder.id)}
                      disabled={actionLoading}
                      className="border-[#E7D9BC] text-[#4E1225] hover:bg-white text-xs font-semibold"
                    >
                      <Printer className="w-3.5 h-3.5 mr-1" />
                      Shipping Label
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenLiveTracking(selectedOrder.id)}
                    className="border-[#E7D9BC] text-[#6E1B34] hover:bg-white text-xs font-semibold"
                  >
                    <Navigation className="w-3.5 h-3.5 mr-1" />
                    Live Tracking
                  </Button>
                </div>
              </div>

              {/* Order Details & Customer info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3.5 rounded-xl space-y-2 border border-gray-100">
                  <h5 className="font-bold text-[#4E1225] uppercase tracking-wider text-[10px]">
                    Customer & Delivery Address
                  </h5>
                  <p className="font-bold text-[#2A1F16]">{selectedOrder.shipping_address?.name}</p>
                  <p className="text-gray-600">{selectedOrder.shipping_address?.address}</p>
                  <p className="text-gray-600">{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} - {selectedOrder.shipping_address?.pincode}</p>
                  <p className="text-gray-600">Phone: {selectedOrder.shipping_address?.phone}</p>
                  <p className="text-gray-600">Email: {selectedOrder.shipping_address?.email || selectedOrder.user?.email}</p>
                </div>

                <div className="bg-gray-50 p-3.5 rounded-xl space-y-1.5 border border-gray-100">
                  <h5 className="font-bold text-[#4E1225] uppercase tracking-wider text-[10px]">
                    Payment Breakdown
                  </h5>
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal:</span>
                    <span>₹{selectedOrder.subtotal?.toLocaleString()}</span>
                  </div>
                  {selectedOrder.discount > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Discount ({selectedOrder.coupon_code}):</span>
                      <span>-₹{selectedOrder.discount?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping ({selectedOrder.shipping_package?.name || 'Standard'}):</span>
                    <span>₹{selectedOrder.shipping_cost || 0}</span>
                  </div>
                  {selectedOrder.gift_wrap_charge > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Gift Wrap:</span>
                      <span>₹{selectedOrder.gift_wrap_charge}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-1 flex justify-between font-bold text-[#6E1B34] text-sm">
                    <span>Total Amount:</span>
                    <span>₹{selectedOrder.total_amount?.toLocaleString()}</span>
                  </div>
                  <p className="text-[10px] text-gray-500 capitalize pt-1">
                    Method: {selectedOrder.payment_method} • Status: {selectedOrder.payment_status}
                  </p>
                </div>
              </div>

              {/* Items List */}
              <div className="border-t border-gray-200 pt-3">
                <h5 className="font-bold text-[#4E1225] uppercase tracking-wider text-[10px] mb-2">
                  Ordered Items ({selectedOrder.items?.length || 0})
                </h5>
                <div className="space-y-1.5">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-bold text-[#2A1F16]">{item.name}</p>
                        <p className="text-[10px] text-gray-500">Qty: {item.quantity} &times; ₹{item.price}</p>
                      </div>
                      <span className="font-bold text-[#4E1225]">₹{(item.subtotal || item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t border-gray-200">
                <Button onClick={() => setSelectedOrder(null)} variant="outline" size="sm">
                  Close
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Live Tracking Modal */}
      <AnimatePresence>
        {trackingModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-2xs flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5 border border-[#E7D9BC] shadow-xl text-xs space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#6E1B34]" />
                  <h4 className="font-serif-heading font-bold text-base text-[#4E1225]">
                    Shiprocket Tracking Details
                  </h4>
                </div>
                <button onClick={() => setTrackingModalOpen(false)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {trackingLoading ? (
                <div className="py-8 text-center">
                  <div className="w-6 h-6 border-2 border-[#6E1B34] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-gray-500">Fetching live courier scans...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-[#FAF7F0] rounded-xl border border-[#E7D9BC] flex items-center justify-between">
                    <div>
                      <span className="text-[10px] text-gray-500 uppercase font-bold">Courier Status</span>
                      <p className="font-bold text-[#4E1225] capitalize">
                        {trackingData?.tracking?.current_status || 'In Transit'}
                      </p>
                    </div>
                    <span className="px-2.5 py-0.5 bg-purple-100 text-purple-800 font-bold rounded-full text-[10px]">
                      {trackingData?.courier_name || 'Shiprocket Partner'}
                    </span>
                  </div>

                  {trackingData?.awb_code && (
                    <p className="text-gray-600">
                      AWB Code: <strong className="font-mono text-[#6E1B34]">{trackingData.awb_code}</strong>
                    </p>
                  )}

                  {/* Scans Timeline */}
                  <div className="space-y-2 border-l-2 border-[#E7D9BC] ml-2 pl-3 pt-1">
                    {trackingData?.tracking?.scans?.map((s, idx) => (
                      <div key={idx} className="relative pb-2">
                        <div className="absolute -left-[17px] top-1 w-2 h-2 rounded-full bg-[#6E1B34]"></div>
                        <p className="font-bold text-[#4E1225]">{s.activity}</p>
                        <p className="text-[10px] text-gray-500">{s.location} • {new Date(s.date).toLocaleString('en-IN')}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end pt-2 border-t border-gray-200">
                    <Button onClick={() => setTrackingModalOpen(false)} size="sm">
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminOrders;
